/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

// The Wimss Dashboard 2 MUI main context
const Wimss = createContext(null);

// Setting custom name for the context which is visible on react dev tools
Wimss.displayName = 'WimssContext';

// Wimss Dashboard 2 MUI reducer
function reducer(state, action) {
  switch (action.type) {
    case 'CURRENT_LANGUAGE': {
      return { ...state, currentLanguage: action.value };
    }
    case 'IS_MOBILE': {
      return { ...state, isMobile: action.value };
    }
    case 'CURRENT_PAGE': {
      return { ...state, currentPage: action.value };
    }
    case 'CURRENT_TAB': {
      return { ...state, currentTab: action.value };
    }
    case 'CURRENT_TEXTS': {
      return { ...state, currentTexts: action.value };
    }
    case 'CURRENT_SLIDES': {
      return { ...state, currentSlides: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Wimss context provider
function WimssControllerProvider({ children }) {
  const initialState = {
    currentLanguage: 'English',
    isMobile: false,
    currentPage: 'home',
    currentTab: 0,
    currentTexts: null,
    currentSlides: null,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <Wimss.Provider value={value}>{children}</Wimss.Provider>;
}

// Wimss custom hook for using context
function useWimssController() {
  const context = useContext(Wimss);

  if (!context) {
    throw new Error(
      'useWimssController should be used inside the WimssControllerProvider.'
    );
  }

  return context;
}

// Typechecking props for the WimssControllerProvider
WimssControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setCurrentLanguage = (dispatch, value) =>
  dispatch({ type: 'CURRENT_LANGUAGE', value });
const setIsMobile = (dispatch, value) => dispatch({ type: 'IS_MOBILE', value });
const setCurrentPage = (dispatch, value) =>
  dispatch({ type: 'CURRENT_PAGE', value });
const setCurrentTab = (dispatch, value) =>
  dispatch({ type: 'CURRENT_TAB', value });
const setCurrentTexts = (dispatch, value) =>
  dispatch({ type: 'CURRENT_TEXTS', value });
const setCurrentSlides = (dispatch, value) =>
  dispatch({ type: 'CURRENT_SLIDES', value });

export {
  WimssControllerProvider,
  useWimssController,
  setCurrentLanguage,
  setIsMobile,
  setCurrentPage,
  setCurrentTab,
  setCurrentTexts,
  setCurrentSlides,
};
